import { isValid, parseISO } from 'date-fns';
import type { Contestation } from '@/integrations/contestacao/client';
import { formatDeadline } from './contestationWindow';

/** Contestação ainda sem conclusão da Musa — a única fase em que existe prazo correndo. */
const IN_PROGRESS_STATUSES = new Set<Contestation['status']>(['OPEN', 'IN_ANALYSIS', 'AWAITING_INFO']);

type DeadlineSource = Pick<Contestation, 'status' | 'conclusion_deadline' | 'expired_at'>;

/** `dd/mm/aaaa` no calendário de Brasília, ou `null` se o valor não é uma data. */
function brasiliaDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = parseISO(value);
  return isValid(date) ? formatDeadline(date) : null;
}

/**
 * Data limite para a Musa concluir, enquanto a contestação está em curso.
 *
 * `null` quando não há o que mostrar: já foi decidida, venceu, ou foi aberta antes do prazo existir
 * (`conclusion_deadline` nulo — ela não vence sozinha, então anunciar uma data seria inventá-la).
 */
export function conclusionDeadlineLabel(contestation: DeadlineSource): string | null {
  if (!IN_PROGRESS_STATUSES.has(contestation.status)) return null;
  return brasiliaDay(contestation.conclusion_deadline);
}

/**
 * Texto da contestação vencida: "Venceu em dd/mm/aaaa sem conclusão dentro do prazo".
 *
 * A data é a do **prazo** (`conclusion_deadline`), não a de `expired_at`: o serviço marca a contestação
 * como vencida logo depois da meia-noite, então `expired_at` cai no dia **seguinte** ao último dia do
 * prazo — mostrá-lo diria ao gerador que ainda havia um dia. `expired_at` fica de reserva, para
 * contestação vencida sem prazo gravado; sem nenhum dos dois o texto sai sem data, em vez de errada.
 */
export function expiredMessage(contestation: DeadlineSource): string | null {
  if (contestation.status !== 'EXPIRED') return null;
  const day = brasiliaDay(contestation.conclusion_deadline) ?? brasiliaDay(contestation.expired_at);
  return day
    ? `Venceu em ${day} sem conclusão dentro do prazo`
    : 'Venceu sem conclusão dentro do prazo';
}
